import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import Sparkline, { extractNumber } from "@/components/Sparkline";

const STATUS_STYLES = {
  open: "bg-slate-100 text-slate-700",
  on_track: "bg-emerald-100 text-emerald-800",
  at_risk: "bg-amber-100 text-amber-800",
  hit: "bg-blue-100 text-blue-800",
  missed: "bg-red-100 text-red-800",
};
const STATUS_ORDER = ["open", "on_track", "at_risk", "hit", "missed"];

function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function MilestoneCard({ m, progress, onLog, onStatus, onDelete }) {
  const [showLog, setShowLog] = useState(false);
  const [month, setMonth] = useState(currentMonth());
  const [actual, setActual] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const rowProgress = progress
    .filter((p) => p.milestone_id === m.id)
    .sort((a, b) => (a.month < b.month ? -1 : 1));

  async function submitLog(e) {
    e.preventDefault();
    if (!actual.trim()) return;
    setSaving(true);
    try {
      await onLog(m.id, { month, actual: actual.trim(), note: note.trim() });
      setActual("");
      setNote("");
      setShowLog(false);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`milestone-${m.id}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="mono-caps text-slate-400 text-[0.65rem] mb-1">
            Due in {m.due_in_days}d · {(m.tied_to_personas || []).join(" · ") || "no persona"}
          </div>
          <h3 className="font-display text-lg leading-tight" data-testid={`milestone-title-${m.id}`}>
            {m.title}
          </h3>
        </div>
        <select
          className="mono-caps text-xs px-2 py-1 rounded-md bg-slate-50 border border-slate-200"
          value={m.status || "open"}
          onChange={(e) => onStatus(m.id, e.target.value)}
          data-testid={`milestone-status-select-${m.id}`}
        >
          {STATUS_ORDER.map((s) => (
            <option key={s} value={s}>
              {s.replace("_", " ").toUpperCase()}
            </option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <div className="mono-caps text-slate-400 text-[0.6rem] mb-1">Metric</div>
          <div className="text-sm text-slate-800">{m.metric}</div>
        </div>
        <div>
          <div className="mono-caps text-slate-400 text-[0.6rem] mb-1">Target</div>
          <div className="text-sm text-slate-800 font-semibold">{m.target}</div>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 mb-3">
        <div className={`mono-caps px-2.5 py-1 rounded-md text-[0.6rem] ${STATUS_STYLES[m.status] || STATUS_STYLES.open}`}>
          {(m.status || "open").replace("_", " ").toUpperCase()}
        </div>
        <Sparkline
          points={rowProgress}
          target={m.target}
          testId={`sparkline-${m.id}`}
        />
      </div>

      {rowProgress.length > 0 && (
        <div className="mt-4 border-t border-slate-100 pt-3">
          <div className="mono-caps text-slate-400 text-[0.6rem] mb-2">Actuals logged</div>
          <ul className="space-y-1">
            {rowProgress.map((p) => (
              <li key={p.id} className="text-xs text-slate-700 flex gap-3" data-testid={`actual-${m.id}-${p.month}`}>
                <span className="mono-caps text-slate-400 shrink-0">{p.month}</span>
                <span className="font-semibold">{p.actual}</span>
                {p.note && <span className="text-slate-500 italic truncate">— {p.note}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-4 flex items-center justify-between">
        <button
          className="mono-caps text-xs text-slate-600 hover:text-slate-900"
          onClick={() => setShowLog((v) => !v)}
          data-testid={`log-actual-toggle-${m.id}`}
        >
          {showLog ? "Cancel" : "+ Log actual"}
        </button>
        <button
          className="mono-caps text-xs text-red-600 hover:text-red-800"
          onClick={() => onDelete(m.id)}
          data-testid={`delete-milestone-${m.id}`}
        >
          Delete
        </button>
      </div>

      {showLog && (
        <form onSubmit={submitLog} className="mt-4 space-y-3 border-t border-slate-100 pt-4">
          <div className="grid grid-cols-3 gap-3">
            <input
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="input-line !py-2 col-span-1"
              data-testid={`actual-month-${m.id}`}
            />
            <input
              type="text"
              value={actual}
              onChange={(e) => setActual(e.target.value)}
              placeholder="Actual (e.g. '1 signed order')"
              className="input-line !py-2 col-span-2"
              data-testid={`actual-value-${m.id}`}
            />
          </div>
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Note (optional)"
            className="input-line !py-2"
            data-testid={`actual-note-${m.id}`}
          />
          <button type="submit" className="btn-primary" disabled={saving} data-testid={`save-actual-${m.id}`}>
            {saving ? "Saving…" : "Save actual"}
          </button>
        </form>
      )}
    </div>
  );
}

function NewMilestoneForm({ onCreate }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [metric, setMetric] = useState("");
  const [target, setTarget] = useState("");
  const [due, setDue] = useState(60);
  const [tied, setTied] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  async function submit(e) {
    e.preventDefault();
    setError(null);
    if (!title.trim() || !metric.trim() || !target.trim()) {
      setError("Title, metric and target are required.");
      return;
    }
    setSaving(true);
    try {
      await onCreate({
        title,
        metric,
        target,
        due_in_days: Number(due) || 60,
        tied_to_personas: tied
          .split(",")
          .map((s) => s.trim().toUpperCase())
          .filter(Boolean),
      });
      setTitle(""); setMetric(""); setTarget(""); setDue(60); setTied("");
      setOpen(false);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not create milestone.");
    } finally {
      setSaving(false);
    }
  }

  if (!open) {
    return (
      <button
        className="btn-ghost"
        onClick={() => setOpen(true)}
        data-testid="new-milestone-toggle"
      >
        + New milestone
      </button>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-xl border border-slate-200 bg-slate-50 p-5 space-y-3" data-testid="new-milestone-form">
      {error && <div className="text-red-700 text-sm">{error}</div>}
      <input
        className="input-line !py-2" placeholder="Milestone title (e.g. 'Sign first wholesale order')"
        value={title} onChange={(e) => setTitle(e.target.value)}
        data-testid="new-milestone-title"
      />
      <div className="grid grid-cols-2 gap-3">
        <input
          className="input-line !py-2" placeholder="Metric (e.g. 'wholesale orders')"
          value={metric} onChange={(e) => setMetric(e.target.value)}
          data-testid="new-milestone-metric"
        />
        <input
          className="input-line !py-2" placeholder="Target (e.g. '1 order')"
          value={target} onChange={(e) => setTarget(e.target.value)}
          data-testid="new-milestone-target"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <input
          type="number" min={1} max={720}
          className="input-line !py-2" placeholder="Due in (days)"
          value={due} onChange={(e) => setDue(e.target.value)}
          data-testid="new-milestone-due"
        />
        <input
          className="input-line !py-2" placeholder="Tied to personas (e.g. S5, S1)"
          value={tied} onChange={(e) => setTied(e.target.value)}
          data-testid="new-milestone-personas"
        />
      </div>
      <div className="flex gap-3">
        <button type="submit" className="btn-primary" disabled={saving} data-testid="new-milestone-save">
          {saving ? "Saving…" : "Add milestone"}
        </button>
        <button type="button" className="btn-ghost" onClick={() => setOpen(false)}>Cancel</button>
      </div>
    </form>
  );
}

export default function Plan() {
  const { id } = useParams();
  const nav = useNavigate();
  const [idea, setIdea] = useState(null);
  const [milestones, setMilestones] = useState([]);
  const [progress, setProgress] = useState([]);
  const [nudges, setNudges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [error, setError] = useState(null);
  const [digestSending, setDigestSending] = useState(false);
  const [digestStatus, setDigestStatus] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [ideaR, planR] = await Promise.all([
          api.get(`/ideas/${id}`),
          api.get(`/ideas/${id}/plan`),
        ]);
        setIdea(ideaR.data.idea);
        setMilestones(planR.data.milestones || []);
        setProgress(planR.data.progress || []);
        setNudges(planR.data.rerun_nudges || []);
      } catch (e) {
        setError(e?.response?.data?.detail || "Could not load plan.");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  async function refreshPlan() {
    const r = await api.get(`/ideas/${id}/plan`);
    setMilestones(r.data.milestones || []);
    setProgress(r.data.progress || []);
    setNudges(r.data.rerun_nudges || []);
  }

  async function createMilestone(payload) {
    await api.post(`/ideas/${id}/plan/milestones`, payload);
    await refreshPlan();
  }

  async function updateStatus(milestoneId, status) {
    try {
      await api.patch(`/ideas/${id}/plan/milestones/${milestoneId}`, { status });
      await refreshPlan();
    } catch (e) {
      // rollback silently — the select will re-render via state
    }
  }

  async function deleteMilestone(milestoneId) {
    if (!window.confirm("Delete this milestone and all its logged actuals?")) return;
    await api.delete(`/ideas/${id}/plan/milestones/${milestoneId}`);
    await refreshPlan();
  }

  async function logProgress(milestoneId, body) {
    await api.post(`/ideas/${id}/plan/milestones/${milestoneId}/progress`, body);
    await refreshPlan();
  }

  async function fetchSuggestions() {
    setSuggesting(true);
    setError(null);
    try {
      const r = await api.post(`/ideas/${id}/plan/suggest`);
      setSuggestions(r.data.suggestions || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Suggestion failed.");
    } finally {
      setSuggesting(false);
    }
  }

  async function acceptSuggestion(sug) {
    await createMilestone({
      title: sug.title,
      metric: sug.metric,
      target: sug.target,
      due_in_days: Number(sug.due_in_days) || 60,
      tied_to_personas: Array.isArray(sug.tied_to_personas) ? sug.tied_to_personas : [],
    });
    setSuggestions((prev) => prev.filter((s) => s !== sug));
  }

  async function sendDigest() {
    setDigestSending(true);
    setDigestStatus(null);
    try {
      const r = await api.post(`/ideas/${id}/digest`);
      setDigestStatus({ ok: true, lines: r.data.line_count || 0 });
    } catch (e) {
      setDigestStatus({ ok: false, message: e?.response?.data?.detail || "Digest send failed." });
    } finally {
      setDigestSending(false);
    }
  }

  const grouped = useMemo(() => {
    const g = { open: [], on_track: [], at_risk: [], hit: [], missed: [] };
    for (const m of milestones) g[m.status || "open"].push(m);
    return g;
  }, [milestones]);

  if (loading) return <div className="p-14 text-slate-500">Loading plan…</div>;

  return (
    <div className="min-h-screen">
      <div className="max-w-[1120px] mx-auto px-8 py-14">
        <button
          className="mono-caps hover:text-slate-900 transition-colors mb-10"
          onClick={() => nav(`/ideas/${id}/verdict`)}
          data-testid="plan-back-to-verdict"
        >
          ← Verdict
        </button>

        <div className="mono-caps mb-4">Plan vs. Actual</div>
        <h1 className="font-display text-4xl sm:text-5xl mb-2">{idea?.title}</h1>
        <p className="text-slate-600 max-w-2xl mb-10">
          Track the specific milestones the panel used to size up your plan. Log actuals monthly.
          When the numbers stop matching the target, close the gap or re-run the panel.
        </p>

        {error && (
          <div className="mb-6 text-red-700 text-sm" data-testid="plan-error">{error}</div>
        )}

        {/* Rerun-ready nudge */}
        {nudges.length > 0 && (
          <div className="mb-8 rounded-xl border border-emerald-300 bg-emerald-50/60 p-6" data-testid="rerun-nudges">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0">
                <div className="mono-caps text-emerald-800 mb-2">Rerun-ready · {new Set(nudges.map(n => n.persona_id)).size} personas</div>
                <div className="text-sm text-slate-800 leading-relaxed mb-3">
                  Your actuals now support items that were open on your Shark Panel. Re-run the
                  panel to see if the verdict flips.
                </div>
                <ul className="space-y-1 mb-4">
                  {nudges.slice(0, 5).map((n, i) => (
                    <li key={i} className="text-xs text-slate-700" data-testid={`rerun-nudge-${n.persona_id}`}>
                      <span className="mono-caps text-emerald-700 mr-2">{n.persona_id}</span>
                      <span className="font-semibold">{n.persona_name}</span>
                      <span className="text-slate-500"> — {n.reason}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <button
                className="btn-primary shrink-0"
                onClick={async () => {
                  try {
                    await api.post(`/ideas/${id}/panel`);
                    nav(`/ideas/${id}/verdict`);
                  } catch (e) {
                    setError(e?.response?.data?.detail || "Panel run failed.");
                  }
                }}
                data-testid="rerun-panel-from-plan"
              >
                Re-run the Shark Panel
              </button>
            </div>
          </div>
        )}

        {/* Digest button */}
        <div className="mb-8 flex items-center justify-between gap-3 flex-wrap">
          <div className="mono-caps text-slate-500 text-[0.65rem]" data-testid="digest-status">
            {idea?.last_digest_sent_at
              ? `Last digest sent ${new Date(idea.last_digest_sent_at).toLocaleDateString()}`
              : "No digest sent yet."}
            {digestStatus?.ok && (
              <span className="ml-3 text-emerald-700">Digest sent ({digestStatus.lines} line{digestStatus.lines === 1 ? "" : "s"}).</span>
            )}
            {digestStatus && !digestStatus.ok && (
              <span className="ml-3 text-red-700">{digestStatus.message}</span>
            )}
          </div>
          <button
            className="btn-ghost"
            onClick={sendDigest}
            disabled={digestSending}
            data-testid="send-digest-btn"
          >
            {digestSending ? "Sending…" : "Email me the digest"}
          </button>
        </div>

        {/* Suggestions row */}
        <div className="mb-10 rounded-xl border border-slate-200 bg-slate-50 p-6" data-testid="plan-suggestions-panel">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
            <div>
              <div className="mono-caps ring-accent-4 mb-1">Suggested by the Shark Panel</div>
              <div className="text-sm text-slate-600">
                Milestones extracted from your Ring 4 transcript and the panel's biggest gaps.
              </div>
            </div>
            <button
              className="btn-ghost"
              disabled={suggesting}
              onClick={fetchSuggestions}
              data-testid="fetch-suggestions-btn"
            >
              {suggesting ? "Thinking…" : suggestions.length ? "Regenerate" : "Suggest milestones"}
            </button>
          </div>
          {suggestions.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4" data-testid="suggestions-list">
              {suggestions.map((s, i) => (
                <div key={i} className="rounded-lg bg-white border border-slate-200 p-4" data-testid={`suggestion-${i}`}>
                  <div className="mono-caps text-slate-400 text-[0.6rem] mb-1">
                    {Array.isArray(s.tied_to_personas) ? s.tied_to_personas.join(" · ") : ""} · due in {s.due_in_days}d
                  </div>
                  <div className="font-semibold text-slate-900 mb-1">{s.title}</div>
                  <div className="text-xs text-slate-600 mb-3">
                    {s.metric} → <span className="font-semibold">{s.target}</span>
                  </div>
                  <button
                    className="mono-caps text-xs text-blue-700 hover:text-blue-900"
                    onClick={() => acceptSuggestion(s)}
                    data-testid={`accept-suggestion-${i}`}
                  >
                    + Add to plan
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Milestone board */}
        <div className="mb-8 flex items-center justify-between">
          <div className="mono-caps text-slate-500" data-testid="milestone-count">
            {milestones.length} milestone{milestones.length === 1 ? "" : "s"} on plan
          </div>
          <NewMilestoneForm onCreate={createMilestone} />
        </div>

        {milestones.length === 0 ? (
          <div className="rounded-xl border border-slate-200 bg-white p-10 text-center" data-testid="empty-plan">
            <p className="text-slate-600 max-w-lg mx-auto">
              No milestones yet. Ask the panel to suggest some, or add your own — each one
              should have a numeric target and a due date.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="milestones-grid">
            {milestones.map((m) => (
              <MilestoneCard
                key={m.id}
                m={m}
                progress={progress}
                onLog={logProgress}
                onStatus={updateStatus}
                onDelete={deleteMilestone}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
